import { type FC, type MouseEvent, useMemo, useState } from 'react';
import { getFormValues, isSubmitting, isValid } from 'redux-form/immutable';
import {
  type BackupFormSaveData,
  type MaintenanceFormSaveData,
  backupFormSave,
  maintenanceFormSave,
  startMaintenance,
  systemClearDb,
} from './actions';
import {
  makeSelectIsLoaded,
  makeSelectSystemBackup,
  makeSelectSystemMaintenance,
} from './selectors';

import { Button } from '@ipfabric/ui-components/Button/Button';
import { LeaveDialog } from '@ipfabric/ui-components/LeaveDialog/LeaveDialog';
import { PageHeader } from '@ipfabric/ui-components/PageHeader/PageHeader';
import { useLocale } from 'containers/App/LocaleContext';
import { ConfirmDialog } from 'containers/ConfirmDialog/ConfirmDialog';
import {
  closeConfirmDialog,
  toggleConfirmDialog,
} from 'containers/ConfirmDialog/actions';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import Immutable from 'immutable';
import { fetchAdvanced } from 'pages/Settings//DiscoverySnapshots/GlobalDiscoveryConfiguration/actions';
import { Helmet } from 'react-helmet';
import { type Selector, connect } from 'react-redux';
import { submit } from 'redux-form';
import { createStructuredSelector } from 'reselect';
import fetch from 'utils/fetch';
import { type AppProps, withAppProps } from 'utils/react/withAppProps';
import { Backup } from './Backup/Backup';
import type { BackupFormValues } from './Backup/Backup';
import { BACKUP_FORM_NAME } from './Backup/constants';
import { ImportExportConfiguration } from './Configuration/ImportExportConfiguration';
import { Maintenance } from './Maintenance/Maintenance';
import type { MaintenanceFormFields } from './Maintenance/Maintenance';
import { MAINTENANCE_FORM_NAME } from './Maintenance/constants';
import {
  CLEAR_DB_DIALOG_NAME,
  RESET_DB_SETTINGS_DIALOG_NAME,
} from './constants';
import type { BackupState, MaintenanceState } from './reducer';
import type { CronFormValue, CronMapValue, CronValueKey } from './types';

// Legacy type for redux-form state compatibility
type LegacyMaintenanceFormValues = {
  enabled: string | boolean;
  cron: CronFormValue;
};

type BackupMaintenanceProps = AppProps & {
  backupFormValues?: BackupFormValues;
  backupState?: Immutable.Record<BackupState>;
  maintenanceFormValues?: LegacyMaintenanceFormValues;
  maintenanceState: Immutable.Record<MaintenanceState>;
  isLoaded?: boolean;
  onlyBackup?: boolean;
  onlyMaintenance?: boolean;
  isBackupValid?: boolean;
  isMaintenanceValid?: boolean;
  saveChangeOnLeave?: boolean;
  isBackupSubmitting?: boolean;

  onCloseConfirmDialog: (x: string) => void;
  onToggleConfirmDialog: (x: string) => void;
  onClearDB: (x: { keepSettings: boolean }) => void;
  onMaintenanceFormSave: (data: MaintenanceFormSaveData) => void;
  onBackupFormSave: (data: BackupFormSaveData) => void;
  onBackupSubmit: () => void;
  onMaintenanceSubmit: () => void;
  onStartMaintenance: () => void;
};

const BareBackupMaintenancePage: FC<BackupMaintenanceProps> = ({
  isBackupSubmitting,
  backupFormValues,
  backupState,
  maintenanceFormValues,
  maintenanceState,
  isLoaded = false,
  isBackupValid,
  isMaintenanceValid,
  saveChangeOnLeave = false,
  onlyBackup = false,
  onlyMaintenance = false,
  ...props
}: BackupMaintenanceProps) => {
  const [isMaintenanceDirty, setIsMaintenanceDirty] = useState(false);
  const locale = useLocale();
  const msg = locale.systemBackupMaintenance;

  const getBackupPristine = () => {
    if (backupFormValues === undefined) {
      return false;
    }

    const normalizeEmptyStringValuesToNull = (value: string | unknown) =>
      value === '' ? null : value;

    // in original backupFormValues, cron is a pure JS object, but in backupState, cron is an Immutable Map.
    // Convert property to Immutable Map to meaningful compare with backupState.
    const backupFormValuesWithSanitizedCron = Immutable.Map<
      string,
      string | boolean | CronFormValue | CronMapValue
    >(backupFormValues).set(
      'cron',
      Immutable.Map<CronValueKey, string>(backupFormValues.cron),
    );

    // There were some "hard to solve" troubles with comparing null and empty strings and since it is from user
    // perspective same and this code will be soon replaced by formik, we decided to do this workaround. In this
    // case we standardize data before comparing so empty string and null will be considered as same value.
    // Unequality caused this bug - https://ipfabric.atlassian.net/browse/NIM-11520
    const backupFormValuesStandardized = backupFormValuesWithSanitizedCron.map(
      normalizeEmptyStringValuesToNull,
    );
    const backupStateStandardized = Immutable.Map(backupState).map(
      normalizeEmptyStringValuesToNull,
    );

    return backupFormValuesStandardized.equals(backupStateStandardized);
  };

  const getMaintenancePristine = () => {
    return !isMaintenanceDirty;
  };

  const getPristine = () => {
    const isBackupPristine = getBackupPristine();
    const isMaintenancePristine = getMaintenancePristine();

    if (onlyBackup) {
      if (backupFormValues?.enabled && backupFormValues.type === 'local') {
        return false;
      }
      return isBackupPristine;
    }

    if (onlyMaintenance) {
      return isMaintenancePristine;
    }

    return isBackupPristine && isMaintenancePristine;
  };

  const closeClearConfirm = () => {
    props.onCloseConfirmDialog(CLEAR_DB_DIALOG_NAME);
  };

  const handleClearDatabaseClick = (e: MouseEvent) => {
    e.preventDefault();
    props.onToggleConfirmDialog(CLEAR_DB_DIALOG_NAME);
  };

  const handleReset = () => {
    closeClearConfirm();
    props.onToggleConfirmDialog(RESET_DB_SETTINGS_DIALOG_NAME);
  };

  const handleCertificationAuthritiesClick = () => {
    props.navigate('/settings/system/certificate-authorities');
  };

  const handleConfirmKeepSettings = () => {
    props.onClearDB({ keepSettings: true });
  };

  const handleConfirmRestoreSettings = () => {
    props.onClearDB({ keepSettings: false });
  };

  const handleMaintenanceSubmit = (values: MaintenanceFormFields) => {
    // Convert plain object to Immutable Map for saga compatibility
    const immutableValues = Immutable.Map({
      enabled: values.enabled,
      cron: values.cron,
    });

    props.onMaintenanceFormSave({
      values: immutableValues,
    });

    setIsMaintenanceDirty(false);
  };

  const handleMaintenanceDirtyChange = (isDirty: boolean) => {
    setIsMaintenanceDirty(isDirty);
  };

  const handleBackupSubmit = (values: BackupFormValues) => {
    const { enabled, cron, type, server, username, password, directory } =
      values;

    if (type === 'local') {
      return props.onBackupFormSave({
        data: {
          enabled,
          type,
          cron,
        },
      });
    }

    return props.onBackupFormSave({
      data: {
        type,
        enabled,
        cron,
        server: server!,
        username: username!,
        password: password!,
        directory: directory!,
      },
    });
  };

  const pristine = getPristine();

  const initialMaintenanceValues = useMemo(() => {
    return maintenanceState !== undefined
      ? (maintenanceState as any).set(
          'cron',
          maintenanceState.get('cron').toJS(),
        )
      : undefined;
  }, [maintenanceState]);

  if (!isLoaded) {
    return (
      <div className='box box-solid mb-0 flex flex-1 items-center'>
        <LoaderAndText />
      </div>
    );
  }

  return (
    <div className='flex flex-col items-center'>
      <Helmet title={msg.pageTitle} />
      <ConfirmDialog
        id={CLEAR_DB_DIALOG_NAME}
        onClickConfirm={handleReset}
        onClickSecondaryConfirm={closeClearConfirm}
        msg={msg.clearDbConfirm}
        btnConfirmVariant='primary'
        btnConfirmAccent='negative'
        btnCancelVariant='secondary'
        btnCancelAccent='positive'
      />
      <ConfirmDialog
        id={RESET_DB_SETTINGS_DIALOG_NAME}
        onClickConfirm={handleConfirmKeepSettings}
        onClickSecondaryConfirm={handleConfirmRestoreSettings}
        msg={msg.resetSettingsConfirm}
        btnConfirmVariant='primary'
        btnConfirmAccent='positive'
        btnCancelVariant='secondary'
        btnCancelAccent='positive'
      />

      <LeaveDialog dirty={!pristine} />

      <PageHeader
        elevated
        width='content-default'
        label='Backup & Maintenance'
        buttons={
          <>
            <Button
              variant='primary'
              accent='negative'
              icon='fa-database'
              onClick={handleClearDatabaseClick}
            >
              Clear Database
            </Button>
            <Button
              variant='outline'
              onClick={handleCertificationAuthritiesClick}
            >
              Show certificate authorities
            </Button>
          </>
        }
      />

      <div className='w-full max-w-content-default flex flex-col px mt-2xl mb gap'>
        <ImportExportConfiguration />
        {onlyBackup ? null : (
          <Maintenance
            initialValues={initialMaintenanceValues}
            onStartMaintenance={props.onStartMaintenance}
            onSubmit={handleMaintenanceSubmit}
            onDirtyChange={handleMaintenanceDirtyChange}
          />
        )}
        {onlyMaintenance ? null : (
          <Backup
            initialValues={
              backupState !== undefined
                ? (backupState as any).set(
                    'cron',
                    backupState.get('cron').toJS(),
                  )
                : undefined
            }
            onSubmit={handleBackupSubmit}
          />
        )}
      </div>
    </div>
  );
};

//@ts-expect-error Hard to type this!
const mapStateToProps = createStructuredSelector({
  backupFormValues: getFormValues(BACKUP_FORM_NAME) as Selector<
    any,
    BackupFormValues
  >,
  backupState: makeSelectSystemBackup(),
  maintenanceFormValues: getFormValues(MAINTENANCE_FORM_NAME) as Selector<
    any,
    LegacyMaintenanceFormValues
  >,
  maintenanceState: makeSelectSystemMaintenance(),
  isLoaded: makeSelectIsLoaded(),
  isBackupSubmitting: isSubmitting(BACKUP_FORM_NAME),
  isBackupValid: isValid(BACKUP_FORM_NAME),
  isMaintenanceValid: isValid(MAINTENANCE_FORM_NAME),
});

function mapDispatchToProps(dispatch: (dispatched: any) => void) {
  return {
    onBackupFormSave: (args: BackupFormSaveData) =>
      dispatch(backupFormSave(args)),
    onBackupSubmit: () => dispatch(submit(BACKUP_FORM_NAME)),
    onClearDB: (args: any) => dispatch(systemClearDb(args)),
    onCloseConfirmDialog: (args: any) => dispatch(closeConfirmDialog(args)),
    onToggleConfirmDialog: (args: any) => dispatch(toggleConfirmDialog(args)),
    onMaintenanceFormSave: (args: MaintenanceFormSaveData) =>
      dispatch(maintenanceFormSave(args)),
    onMaintenanceSubmit: () => dispatch(submit(MAINTENANCE_FORM_NAME)),
    onStartMaintenance: () => dispatch(startMaintenance()),
  };
}

export const BackupMaintenancePage = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withAppProps(fetch({ actions: [fetchAdvanced] })(BareBackupMaintenancePage)));
